// src/components/OppTable.jsx
import React, { useMemo, useState } from "react";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Collapse,
  Chip,
  TablePagination,
  TextField,
  Typography,
  Divider,
} from "@mui/material";
import { KeyboardArrowDown, KeyboardArrowUp } from "@mui/icons-material";

/* ---------- small helpers / color map ---------- */
const STATUS_STYLE = {
  Active: {
    bgcolor: "#E6F4EA",
    color: "#1B7A36",
    border: "1px solid rgba(27,122,54,0.12)",
  },
  Inactive: {
    bgcolor: "#FFF4E0",
    color: "#B36D00",
    border: "1px solid rgba(179,109,0,0.12)",
  },
  Open: {
    bgcolor: "#FFF6E8",
    color: "#A86C00",
    border: "1px solid rgba(168,108,0,0.12)",
  },
  Default: {
    bgcolor: "#F0F0F0",
    color: "#333",
    border: "1px solid rgba(0,0,0,0.06)",
  },
};

/* ---------- Status pill component (filled style) ---------- */
const StatusPill = ({ value = "" }) => {
  const style = STATUS_STYLE[value] || STATUS_STYLE.Default;
  return (
    <Chip
      label={value}
      size="small"
      sx={{
        backgroundColor: style.bgcolor,
        color: style.color,
        borderRadius: "6px",
        fontWeight: 600,
        px: 1.2,
        py: 0.35,
        border: style.border,
        boxShadow: "none",
        height: 26,
      }}
    />
  );
};

/* ---------- Expandable row (main + nested sub-table) ---------- */
const ExpandableRow = ({ row, columns }) => {
  const [open, setOpen] = useState(false);

  return (
    <>
      <TableRow
        sx={{
          "&:nth-of-type(odd)": { backgroundColor: "#FBFBFB" },
          "&:hover": { backgroundColor: "#F3F3F3" },
          verticalAlign: "middle",
        }}
      >
        <TableCell sx={{ width: 56, px: 1 }}>
          <IconButton
            size="small"
            onClick={() => setOpen((s) => !s)}
            aria-label="expand row"
          >
            {open ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
          </IconButton>
        </TableCell>

        {columns.map((col, idx) => {
          const content =
            col.type === "status" ? (
              <StatusPill value={row[col.field]} />
            ) : (
              row[col.field]
            );

          // alignment shortcuts
          const align = col.align || "left";
          const cellSx = {
            fontSize: 14,
            py: 1.2,
            borderBottom: "1px solid #E7E7E7",
            verticalAlign: "middle",
            textAlign: align,
          };

          // description column: ellipsis
          if (col.field === "description") {
            cellSx.maxWidth = 320;
            cellSx.whiteSpace = "nowrap";
            cellSx.overflow = "hidden";
            cellSx.textOverflow = "ellipsis";
          }

          // ID column purple styling
          if (col.field === "id") {
            cellSx.color = "#6b1d5c";
            cellSx.fontWeight = 600;
          }

          // center small numeric columns
          if (col.field === "rows") {
            cellSx.textAlign = "center";
          }

          return (
            <TableCell key={idx} sx={cellSx}>
              {content}
            </TableCell>
          );
        })}
      </TableRow>

      {/* Expanded / nested table */}
      <TableRow>
        <TableCell
          colSpan={columns.length + 1}
          sx={{ py: 0, borderBottom: "1px solid #E7E7E7" }}
        >
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ m: 1, borderLeft: "4px solid #6b1d5c" }}>
              <Table
                size="medium"
                sx={{
                  backgroundColor: "#FAFAFA",
                  borderRadius: 1,
                  "& .MuiTableCell-root": {
                    py: 1.2,  
                  },
                  "& .MuiTableRow-root": {
                    height: 55, 
                  },
                }}
        
              >
                <TableHead>
                  <TableRow sx={{ backgroundColor: "#D6D6D6" }}>
                    <TableCell sx={{ fontWeight: 700 }}>Bid Row #</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>
                      Bid Row Status
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>
                      Role / Solution
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Associate</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>
                      Associate Status
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700, textAlign: "right" }}>
                      Hourly Rate (USD)
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>T&E</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>POP Start</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>POP End</TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {row?.subRows?.map((sr, i) => (
                    <TableRow key={i} size="large" hover>
                      <TableCell sx={{ py: 0.8 }}>{sr.bidRow}</TableCell>
                      <TableCell sx={{ py: 0.8 }}>
                        <StatusPill value={sr.status} />
                      </TableCell>
                      <TableCell sx={{ py: 0.8 }}>{sr.role}</TableCell>
                      <TableCell
                        sx={{ py: 0.8, color: "#6b1d5c", fontWeight: 600 }}
                      >
                        {sr.associate}
                      </TableCell>
                      <TableCell sx={{ py: 0.8 }}>
                        {sr.associateStatus}
                      </TableCell>
                      <TableCell sx={{ py: 0.8, textAlign: "right" }}>
                        ${sr.hourlyRate}
                      </TableCell>
                      <TableCell sx={{ py: 0.8 }}>{sr.tAndE}</TableCell>
                      <TableCell sx={{ py: 0.8 }}>{sr.popStart}</TableCell>
                      <TableCell sx={{ py: 0.8 }}>{sr.popEnd}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
};

/* ---------- full table with header, search and pagination ---------- */
const OppTable = ({ columns = [], rows = [] }) => {
  // local state: search + pagination
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);

  const filteredRows = useMemo(() => {
    if (!search) return rows;
    const q = search.toLowerCase();
    return rows.filter(
      (r) =>
        String(r.id || "")
          .toLowerCase()
          .includes(q) ||
        String(r.draftName || "")
          .toLowerCase()
          .includes(q) ||
        String(r.description || "")
          .toLowerCase()
          .includes(q)
    );
  }, [rows, search]);

  const paginatedRows = useMemo(() => {
    if (rowsPerPage <= 0) return filteredRows;
    return filteredRows.slice(
      page * rowsPerPage,
      page * rowsPerPage + rowsPerPage
    );
  }, [filteredRows, page, rowsPerPage]);

  const handleChangePage = (_, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  return (
    <Paper sx={{ boxShadow: "none", width:"100%" }}>
   
 
      <TableContainer
        component={Paper}
        sx={{ boxShadow: "none", border: "1px solid #E8E8E8", borderRadius: 2 }}
      >
        <Table>
          <TableHead sx={{ backgroundColor: "#6b1d5c" }}>
            <TableRow>
              <TableCell sx={{ width: 56 }} /> {/* expand column */}
              {columns.map((col, i) => (
                <TableCell
                  key={i}
                  sx={{
                    color: "#fff",
                    fontWeight: 700,
                    textTransform: "uppercase",
                    fontSize: 13,
                    borderRight: "1px solid rgba(255,255,255,0.06)",
                  }}
                >
                  {col.header}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>

          <TableBody>
            {paginatedRows.map((row, idx) => (
              <ExpandableRow key={row.id ?? idx} row={row} columns={columns} />
            ))}

            {/* handle empty */}
            {paginatedRows.length === 0 && (
              <TableRow>
                <TableCell
                  colSpan={columns.length + 1}
                  sx={{ py: 4, textAlign: "center", color: "#666" }}
                >
                  No results
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          m: 1,
        }}
      >
        <Typography variant="body2" sx={{ color: "#666" }}>
          {filteredRows.length
            ? `1-${Math.min(
                filteredRows.length,
                (page + 1) * rowsPerPage
              )} of ${filteredRows.length} results`
            : "0 results"}
        </Typography>

        <TablePagination
          component="div"
          count={filteredRows.length}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[5, 10, 25, 100]}
          labelRowsPerPage="Rows per page"
          sx={{
            "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows":
              { fontSize: 13 },
          }}
        />
      </Box>
    </Paper>
  );
};

export default OppTable;
